import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Leaf, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

const VALUES = [
  {
    icon: Heart,
    titleKey: 'story.values.1.title',
    descKey: 'story.values.1.description',
    bgKey: 'story.values.background1',
    defaultBg: '/assets/stock/value-1-ships.jpg',
  },
  {
    icon: Shield,
    titleKey: 'story.values.2.title',
    descKey: 'story.values.2.description',
    bgKey: 'story.values.background2',
    defaultBg: '/assets/stock/value-2-stall.jpg',
  },
  {
    icon: Leaf,
    titleKey: 'story.values.3.title',
    descKey: 'story.values.3.description',
    bgKey: 'story.values.background3',
    defaultBg: '/assets/stock/value-3-boxes.jpg',
  },
];

export default function Story() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('/story');
  }, []);

  const { getValue, getHtml, loading: cmsLoading } = useCMS();

  const galleryImages = [
    {
      src: getValue('story.gallery.image1', '/assets/stock/story-g1-dumplings.jpg'),
      caption: getValue('story.gallery.caption1', '直播試貨——真人試過先上架，直播間就是櫥窗'),
      span: 'md:col-span-2 md:row-span-2',
    },
    {
      src: getValue('story.gallery.image2', '/assets/stock/story-g2-shopkeeper.jpg'),
      caption: getValue('story.gallery.caption2', '香港中小企品牌——逐家嚴選，貨源把關'),
      span: '',
    },
    {
      src: getValue('story.gallery.image3', '/assets/stock/story-g3-warehouse.jpg'),
      caption: getValue('story.gallery.caption3', '荃灣倉庫——貨到即分揀，直播訂單即出'),
      span: '',
    },
    {
      src: getValue('story.gallery.image4', '/assets/stock/story-g4-cranes.jpg'),
      caption: getValue('story.gallery.caption4', '包稅專線——食品敏感貨可走，清關一條龍'),
      span: '',
    },
    {
      src: getValue('story.gallery.image5', '/assets/stock/story-g5-delivery.jpg'),
      caption: getValue('story.gallery.caption5', '原箱直郵——英加澳美，海外港人門口收貨'),
      span: 'md:col-span-2',
    },
  ];

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white py-20 md:py-28 overflow-hidden">
        {/* 背景：客人素材未齊，先用品牌深綠漸層（素材包到位後 CMS 換圖） */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(120% 140% at 15% 0%, #1a6b50 0%, #12503c 38%, #0b3527 100%)' }}
        />
        <div className="absolute inset-0 bg-x1493-dark/40" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <p className="text-x1493-secondary font-bold tracking-widest mb-4">品牌故事</p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            dangerouslySetInnerHTML={getHtml('story.hero.title', '由跨境物流，到香港品牌出海航道')}
          />
          <p
            className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto"
            dangerouslySetInnerHTML={getHtml('story.hero.subtitle', 'YYC Global Logistics 十年跨境專線，把積累的物流資源，變成香港中小企品牌的出海航道——直播試貨平台由此而生。')}
          />
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('story.gallery.title', '從原料到成品，每一步都看得見')}
            />
            <p
              className="text-stone-600 max-w-2xl mx-auto"
              dangerouslySetInnerHTML={getHtml('story.gallery.description', '由跨境物流積累，到直播試貨平台——三道護城河，環環相扣。（後台 CMS 可編輯）')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[200px]">
            {galleryImages.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setLightboxImage(item.src)}
                className={`relative rounded-2xl overflow-hidden group text-left ${item.span || ''}`}
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  className="absolute inset-0 w-full h-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                    點擊放大
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <p className="text-white font-medium text-sm md:text-base leading-snug">
                    {item.caption}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Lightbox */}
          {lightboxImage && (
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setLightboxImage(null)}
            >
              <button
                type="button"
                onClick={() => setLightboxImage(null)}
                className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
                aria-label="關閉"
              >
                <X size={32} />
              </button>
              <img
                src={lightboxImage}
                alt="放大預覽"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('story.values.title', '我們的堅持')}
            />
            <p
              className="text-stone-600"
              dangerouslySetInnerHTML={getHtml('story.values.subtitle', '為什麼香港品牌出海，要揀 YYC 直播試貨平台？')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, titleKey, descKey, bgKey, defaultBg }) => (
              <div
                key={titleKey}
                className="relative rounded-2xl p-8 overflow-hidden min-h-[320px] flex flex-col group"
              >
                <img
                  src={getValue(bgKey, defaultBg)}
                  alt={getValue(titleKey)}
                  className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/40" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="text-x1493-secondary" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{getValue(titleKey)}</h3>
                  <p className="text-white/80">{getValue(descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-x1493-primary text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            dangerouslySetInnerHTML={getHtml('story.cta.title', '準備好開始了嗎？')}
          />
          <p
            className="text-white/90 mb-8"
            dangerouslySetInnerHTML={getHtml('story.cta.subtitle', '入貨架睇真貨——多品牌嚴選，YYC 包稅直郵全球。')}
          />
          <Link to="/store" className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition">
            {getValue('story.cta.buttonText', '逛商店')}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
